import { useEffect, useState } from 'react';
import { useSaveToasts } from '../use-save-toasts';
import {
  currencySettingsSchema,
  externalCostSchema,
  externalCostSummarySchema,
  formatMoney,
  formatSort,
  lineItemSchema,
  metafieldSchema,
  occurrenceEconomicsSchema,
  type ExternalCost,
  type Metafield,
  type Project,
  type SaveExternalCostRequest,
} from '@forecastify/shared';
import { DataList, type DataListColumn, type DataListRow, type SortState } from '@forecastify/ui';
import { useServerList, useServerSummary } from '../use-server-list';
import { useT } from '../../i18n';
import { CONTRACT_TOPICS } from '../../live/topics';
import { useLiveInvalidation } from '../../live/use-live';
import { useSession } from '../../session';
import { useWorkspaceNavigate } from '../../workspace-path';
import { managesProject, parseList, useMyMemberId, usePermissionSet } from '../permissions-hook';
import { listLabels } from '../leave/requests-screen';
import {
  ExternalCostDialog,
  bodyOfCost,
  draftOf,
  emptyDraft,
  type ExternalCostDraft,
} from './external-cost-dialog';
import { costLinkLabel, costRecurrenceLabel } from './external-cost-detail';
import { ExternalCostSurface, type ExternalCostMode } from './external-cost-surface';
import { lineItemContractLabel } from './contract-label';
import { personCell } from '../person-cell';
import { requestAll } from '../read-all';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A projekt Külső költségek fülje — C56.                                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **`DataList`, LIKE EVERY OTHER TABLE IN THIS PRODUCT.** The 15 Sep round settled it in one
 * sentence — *"why is this yet another way of doing this when we already have a table view that
 * fits here with sortable columns and filtering+search? use that here aswell man"* — and the mockup
 * draws exactly that shape here anyway.
 *
 * ⚠️ **The footer sums the TOTAL, not the amount.** A recurring licence's row shows 180 000 Ft and
 * contributes 2 160 000 to the sum; the mockup's own footer says so — *"Σ 4 361 600 Ft (a teljes
 * futamidőre)"* — and a sum of the amounts would be a figure nobody could reconcile with anything.
 *
 * ⚠️ **31 Oct 2026 · one page at a time, from the server.** The search and the sort are query
 * parameters and the footer is `/external-costs/summary`'s, over the whole filtered set — a sum of
 * the page on screen would be a figure about rows nobody can see.
 */
export function ProjectCostsTab({ project }: { readonly project: Project }) {
  const t = useT();
  /* 26 Oct 2026 · the cost dialog's success toast. */
  const { saved, toaster } = useSaveToasts();
  const go = useWorkspaceNavigate();
  const session = useSession();
  const permissions = usePermissionSet();

  /* Bumped by every write and every live change — the page and the footer re-read. */
  const [nonce, setNonce] = useState(0);
  const [sort, setSort] = useState<readonly SortState[]>([]);
  const [lineItems, setLineItems] = useState<readonly { id: string; name: string }[]>([]);
  const [contracts, setContracts] = useState<readonly { id: string; code: string }[]>([]);
  const [periods, setPeriods] = useState<readonly { startsOn: string; label: string }[]>([]);
  const [currencies, setCurrencies] = useState<
    readonly { code: string; symbol: string; rateToBase: string }[]
  >([]);
  const [members, setMembers] = useState<readonly { id: string; name: string }[]>([]);
  /* C62 · `0132` put `external_cost` in `metafield_entity`, and until the detail screen existed
     nothing rendered what an admin defined there. */
  const [metafields, setMetafields] = useState<readonly Metafield[]>([]);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<ExternalCostDraft | null>(null);
  /* ux-pass-2 · the detail opens the dialog for ONE question — the link, or the recurrence. */
  const [focus, setFocus] = useState<'link' | 'recurrence' | undefined>(undefined);
  const [openCost, setOpenCost] = useState<string | null>(null);
  const [mode, setMode] = useState<ExternalCostMode>('drawer');
  const [serverError, setServerError] = useState<string | null>(null);

  const filtered = { projectId: project.id, search: query.trim() };
  const list = useServerList({
    path: '/external-costs',
    params: { ...filtered, sort: formatSort(sort) ?? '' },
    schema: externalCostSchema,
    nonce,
  });
  const costs = list.rows;
  const summary = useServerSummary({
    path: '/external-costs/summary',
    params: filtered,
    schema: externalCostSummarySchema,
    nonce,
  });

  useLiveInvalidation(CONTRACT_TOPICS, () => {
    setNonce((current) => current + 1);
  });

  /* ⚠️ The pickers, once. They do not depend on the list, and re-reading every line item in the
     workspace each time somebody typed in the search would be four requests for a dropdown. */
  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const [itemResponse, currencyResponse, memberResponse, fieldResponse] = await Promise.all([
        session.request('/line-items').catch(() => null),
        session.request('/workspace/currencies').catch(() => null),
        requestAll(session, '/people').catch(() => null),
        session.request('/metafields?entity=external_cost').catch(() => null),
      ]);

      if (fieldResponse?.ok === true) {
        setMetafields(parseList(metafieldSchema, await fieldResponse.json()) ?? []);
      }

      if (itemResponse?.ok === true) {
        const parsed = parseList(lineItemSchema, await itemResponse.json()) ?? [];
        const mine = parsed.filter((one) => one.projectId === project.id);
        setLineItems(mine.map((one) => ({ id: one.id, name: one.name })));
        /* ⚠️ The contracts this project HAS, derived from its line items rather than read again —
           a cost's occurrence arm can only point at a contract of this project. */
        setContracts(
          [
            ...new Map(mine.map((one) => [one.contractId, lineItemContractLabel(one)])).entries(),
          ].map(([id, code]) => ({ id, code })),
        );
      }
      if (currencyResponse?.ok === true) {
        const parsed = currencySettingsSchema.safeParse(await currencyResponse.json());
        if (parsed.success) {
          setCurrencies(
            parsed.data.currencies
              .filter((one) => one.active)
              .map((one) => ({
                code: one.code,
                symbol: one.symbol,
                rateToBase: one.rateToBase,
              })),
          );
        }
      }
      if (memberResponse?.ok === true) {
        const body = (await memberResponse.json()) as { data?: unknown };
        if (Array.isArray(body.data)) {
          setMembers(
            body.data
              .filter(
                (one): one is { id: string; displayName: string } =>
                  typeof one === 'object' &&
                  one !== null &&
                  typeof (one as { id?: unknown }).id === 'string',
              )
              .map((one) => ({ id: one.id, name: one.displayName })),
          );
        }
      }
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); depending on it refetches for ever. */
  }, [session.workspaceId, project.id]);

  /**
   * ⚠️ **The periods of whichever contract the dialog is pointing at.** Read when the pick changes
   * rather than for every contract up front: a workspace with ten frameworks would otherwise fetch
   * two hundred periods to fill a dropdown nobody opened.
   */
  const pickedContract = editing?.contractId ?? '';
  useEffect(() => {
    setPeriods([]);
    if (pickedContract === '' || session.workspaceId === null) return;
    void (async () => {
      const response = await session
        .request(`/contracts/${pickedContract}/economics`)
        .catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(occurrenceEconomicsSchema, await response.json()) ?? [];
      setPeriods(
        parsed.map((one) => ({
          startsOn: one.startsOn,
          label: `#${String(one.index)} · ${one.startsOn}`,
        })),
      );
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- see above. */
  }, [pickedContract, session.workspaceId]);

  /* 22 Oct 2026 · a project reader without its money gets the costs, not their figures. */
  const showMoney = summary?.moneyVisible ?? costs.every((cost) => cost.moneyVisible);
  const money = (amount: string | null, code: string | null): string => {
    if (amount === null || code === null) return '';
    const symbol = currencies.find((one) => one.code === code)?.symbol ?? code;
    return formatMoney(Number(amount), { code, symbol });
  };

  /* ⚠️ C62 · both label helpers live beside the DETAIL now, so the column and the screen it opens
     cannot word the same fact differently. */
  const linkLabel = (cost: ExternalCost): string => costLinkLabel(t, cost);
  const recurrenceLabel = (cost: ExternalCost): string => costRecurrenceLabel(t, cost);

  const allColumns: DataListColumn[] = [
    { key: 'code', label: t('list.identifier'), width: 120, sortKey: 'code' },
    { key: 'name', label: t('extcost.column.name'), width: 240, sortKey: 'name' },
    { key: 'date', label: t('extcost.column.date'), width: 110, sortKey: 'date' },
    {
      key: 'amount',
      label: t('extcost.column.amount'),
      width: 130,
      align: 'right',
      sortKey: 'amount',
    },
    { key: 'link', label: t('extcost.column.link'), width: 200, sortKey: 'link' },
    {
      key: 'recurrence',
      label: t('extcost.column.recurrence'),
      width: 130,
      sortKey: 'recurrence',
    },
    {
      key: 'total',
      label: t('extcost.column.total'),
      width: 140,
      align: 'right',
      sortKey: 'total',
    },
    { key: 'owner', label: t('extcost.column.owner'), width: 160, sortKey: 'owner' },
  ];
  const columns = showMoney
    ? allColumns
    : allColumns.filter((column) => column.key !== 'amount' && column.key !== 'total');

  /**
   * ⚠️ **In the server's order** — `page` in `external-cost.service.ts` sorts on the cost's own
   * fields, the whole-term total included, so the money does not sort by its separator.
   */
  const rows: DataListRow[] = (() => {
    return costs.map((cost) => ({
      id: cost.id,
      cells: {
        code: { text: cost.identifier, mono: true, muted: true },
        name: { text: cost.name, bold: true },
        date: { text: cost.incurredOn, mono: true },
        amount: { text: money(cost.amount, cost.currencyCode), mono: true },
        link: { text: linkLabel(cost) },
        recurrence: {
          badge: {
            label: recurrenceLabel(cost),
            variant: cost.recurrenceUnit === null ? 'outline' : 'secondary',
          },
        },
        /* ⚠️ The MULTIPLIED figure, and muted where it equals the amount — a one-off repeating
             its own number in two columns is noise the eye has to dismiss on every row. */
        total: {
          text: money(cost.total, cost.currencyCode),
          mono: true,
          muted: cost.periods === 1,
        },
        owner: personCell(cost.ownerName, { seed: cost.ownerAvatarSeed }),
      },
    }));
  })();

  /**
   * ⚠️⚠️ **THE FOOTER CONVERTS, AND THE FIRST VERSION DID NOT.** It summed 2 400 EUR and
   * 640 000 HUF into 642 400 of nothing — caught by reading the real fixture, where one row is a
   * European supplier. `fxRate` is `0126`'s one-column-per-currency rule, so a cross rate cannot
   * disagree with itself; a pair with no rate falls back to 1:1, which is the same lesser-of-two-
   * wrongs `economics.service.ts` argues for.
   *
   * ⚠️ 20 Oct 2026 · FX-3 · **the server converts now, each charge at its own day's rate** —
   * `converted`, in the workspace's default. Multiplying here by today's rate re-priced every past
   * licence whenever the euro moved.
   */
  const currencyCode =
    summary?.currencyCode ??
    currencies.find((one) => one.rateToBase === '1.00000000')?.code ??
    'HUF';
  const count = summary?.count ?? 0;
  const total = Number(summary?.total ?? 0);
  /* 22 Oct 2026 · a cost is saved whole, amount and all — not by somebody who cannot see it. */
  /* 22 Oct 2026 · at `own`, only on a project the reader is PM of (`project-manage.ts`). */
  const myMemberId = useMyMemberId();
  const canManage =
    managesProject(permissions, 'contract.manage', project.pmMemberId, myMemberId) && showMoney;
  const opened = costs.find((one) => one.id === openCost) ?? null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {serverError === null ? null : (
        <p className="text-destructive px-4 pt-2 text-[12px]">{serverError}</p>
      )}

      <DataList
        columnsKey="project-costs"
        card={{
          title: 'name',
          context: 'code',
          defaults: {
            avatar: 'owner',
            details: showMoney
              ? ['amount', 'date', 'link', 'recurrence']
              : ['date', 'link', 'recurrence'],
          },
        }}
        isLoading={list.loading}
        columns={columns}
        rows={rows}
        page={list.page}
        sort={sort}
        onSortChange={setSort}
        labels={listLabels(t)}
        search={query}
        onSearchChange={setQuery}
        empty={{
          icon: 'credit-card',
          title: t('extcost.emptyProject'),
          body: t('extcost.dialog.hint'),
          ...(canManage
            ? {
                actionLabel: t('extcost.new'),
                onAction: () => {
                  setEditing(emptyDraft(project.id, currencyCode, members[0]?.id ?? ''));
                },
              }
            : {}),
        }}
        /**
         * ⚠️⚠️ **A ROW OPENS THE COST — C62 REPLACED THE PANEL THIS COMMENT USED TO DEFEND.** It
         * argued that a strip of attachments under the row was the same document one click closer
         * and needed no address; what it actually did was leave the mockup's whole detail screen
         * unbuilt, so the periods a recurring cost lands on and its metafields had nowhere to be
         * drawn. The drawer is the mockup's own default (`ecMode`), and the full view has an
         * address now.
         */
        onRowClick={(id) => {
          setMode('drawer');
          setOpenCost((current) => (current === id ? null : id));
        }}
        rowMenu={
          !canManage
            ? undefined
            : (id) => {
                const cost = costs.find((one) => one.id === id);
                if (cost === undefined) return [];
                return [
                  {
                    label: t('extcost.action.edit'),
                    icon: 'pencil',
                    onSelect: () => {
                      setFocus(undefined);
                      setEditing(draftOf(cost));
                    },
                  },
                  {
                    label: t('extcost.action.delete'),
                    icon: 'trash',
                    destructive: true,
                    onSelect: () => {
                      void write(`/external-costs/${cost.id}`, { method: 'DELETE' });
                    },
                  },
                ];
              }
        }
        /* ⚠️ The toolbar's own action slot rather than a button beside the table — every other
           list in the product puts its primary action there, and one placed differently here is
           the first thing a reader has to look for twice. */
        /* ⚠️⚠️ **THE FOOTER SUMS THE TOTAL, NOT THE AMOUNT.** A recurring licence's row shows
           180 000 Ft and contributes 2 160 000 — the mockup's own footer says so, and a sum of the
           amounts would be a figure nobody could reconcile with the column above it. */
        footerNote={
          showMoney
            ? `${t('extcost.count', { count })} · ${t('extcost.sum', {
                amount: money(total.toFixed(2), currencyCode),
              })}`
            : t('extcost.count', { count })
        }
        actions={
          !canManage
            ? []
            : [
                {
                  label: t('extcost.new'),
                  icon: 'plus',
                  /* ⚠️ The tab's main action: the right edge, in the brand fill. */
                  primary: true,
                  onSelect: () => {
                    setFocus(undefined);
                    setEditing(emptyDraft(project.id, currencyCode, members[0]?.id ?? ''));
                  },
                },
              ]
        }
      />

      {opened === null ? null : (
        <ExternalCostSurface
          mode={mode}
          open
          data={{ cost: opened, metafields, members, currencies }}
          onModeChange={(next) => {
            /* ⚠️ **Full is a NAVIGATION, not a third frame rendered here.** The page owns the
               address; rendering the wide body inside the tab would put two screens at one URL,
               which is the drift `task-surface.tsx` warns about. */
            if (next === 'full') {
              setOpenCost(null);
              go(`/costs/${opened.identifier}`);
              return;
            }
            setMode(next);
          }}
          onClose={() => {
            setOpenCost(null);
          }}
          onNavigate={(path) => {
            setOpenCost(null);
            go(path);
          }}
          handlers={{
            canManage,
            onPatch: (change) => {
              void save(opened.id, bodyOfCost(opened, change));
            },
            onEditLink: () => {
              setFocus('link');
              setEditing(draftOf(opened));
            },
            onEditRecurrence: () => {
              setFocus('recurrence');
              setEditing(draftOf(opened));
            },
            onDuplicate: () => {
              setFocus(undefined);
              /* ⚠️ A copy carries the LINK and the shape, not the number: `emptyDraft` then the
                 fields somebody would otherwise retype. The mockup's own *Duplikálás*. */
              setEditing({ ...draftOf(opened), id: null });
            },
            onDelete: () => {
              setOpenCost(null);
              void write(`/external-costs/${opened.id}`, { method: 'DELETE' });
            },
            onMetafields: (values) => {
              void save(opened.id, bodyOfCost(opened, { metafields: values }));
            },
            onOpenProject: () => {
              go(`/projects/${opened.projectId}`);
            },
            onOpenLink: () => {
              openLink(opened);
            },
            onOpenOwner: () => {
              go(`/people/${opened.ownerMemberId}`);
            },
            onOpenPeriod: (startsOn) => {
              if (opened.contractIdentifier !== null) {
                go(`/contracts/${opened.contractIdentifier}/periods/${startsOn}`);
              }
            },
            onAttach: async (file) => {
              const form = new FormData();
              form.set('file', file);
              const response = await session
                .request(`/files?target=external_cost&targetId=${opened.id}`, {
                  method: 'POST',
                  body: form,
                })
                .catch(() => null);
              return response?.ok === true ? null : 'upload_failed';
            },
            onFull: () => {
              setOpenCost(null);
              go(`/costs/${opened.identifier}`);
            },
          }}
        />
      )}

      {toaster}
      {editing === null ? null : (
        <ExternalCostDialog
          draft={editing}
          currencies={currencies}
          lineItems={lineItems}
          contracts={contracts}
          periods={periods}
          members={members}
          serverError={serverError}
          {...(focus === undefined ? {} : { focus })}
          onCancel={() => {
            setEditing(null);
            setFocus(undefined);
          }}
          onSave={(body) => {
            void save(editing.id, body);
          }}
        />
      )}
    </div>
  );

  /**
   * ⚠️ **A line item's address is its contract's identifier AND its own** (R-57). A
   * project-linked cost has nowhere to go, and the control that calls this is disabled for it.
   */
  function openLink(cost: ExternalCost): void {
    if (cost.linkKind === 'line_item') {
      if (cost.contractIdentifier !== null && cost.lineItemIdentifier !== null) {
        go(`/contracts/${cost.contractIdentifier}/${cost.lineItemIdentifier}`);
      }
      return;
    }
    if (cost.linkKind === 'occurrence' && cost.contractIdentifier !== null) {
      go(`/contracts/${cost.contractIdentifier}/periods/${cost.occurrenceStartsOn ?? ''}`);
    }
  }

  /** 26 Oct 2026 · resolves to whether it landed — the dialog closes on `true` only. */
  async function write(path: string, init: RequestInit): Promise<boolean> {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response?.ok !== true) {
      /* ⚠️ A stale write is its own sentence: an in-place edit races the live reload. */
      setServerError(response?.status === 409 ? t('error.conflict') : t('error.unavailable'));
      return false;
    }
    setNonce((current) => current + 1);
    return true;
  }

  async function save(id: string | null, body: SaveExternalCostRequest): Promise<void> {
    const landed = await write(id === null ? '/external-costs' : `/external-costs/${id}`, {
      method: id === null ? 'POST' : 'PUT',
      body: JSON.stringify(body),
    });
    /* 26 Oct 2026 · ⚠️ it closed whatever the answer, so a refusal was never seen. It stays
       open now, with the refusal in it, and a save that landed says so. */
    if (!landed) return;
    setEditing(null);
    setFocus(undefined);
    saved(
      id === null
        ? t('common.toast.created', { name: body.name })
        : t('common.toast.saved', { name: body.name }),
    );
  }
}
